// Client-side counterpart of `seo-html-rewrite.ts`.
//
// TanStack Router owns the <head> after hydration and re-renders every meta
// tag from each route's head(). That wiped the admin overrides the server had
// injected into the SSR HTML, and left the server-rendered tags behind as
// duplicates. This module re-asserts the same override on the live document
// and removes duplicate tags, so server- and client-rendered metadata agree.
import type { PageSeoOverride } from "./page-seo";

type Doc = Document;

const META_KEYS: Array<{ kind: "name" | "property"; key: string }> = [
  { kind: "name", key: "description" },
  { kind: "property", key: "og:title" },
  { kind: "property", key: "og:description" },
  { kind: "property", key: "og:image" },
  { kind: "property", key: "og:url" },
  { kind: "name", key: "twitter:title" },
  { kind: "name", key: "twitter:description" },
  { kind: "name", key: "twitter:image" },
  { kind: "name", key: "robots" },
];

function selector(kind: "name" | "property", key: string) {
  return `meta[${kind}="${key.replace(/"/g, '\\"')}"]`;
}

/**
 * Keeps the LAST tag for each managed key and removes the rest. The last one
 * is the router's freshly rendered tag for the current route; earlier copies
 * are leftovers from the previous page or from the SSR document.
 */
export function dedupeHeadTags(doc: Doc): number {
  let removed = 0;
  const drop = (nodes: Element[]) => {
    nodes.slice(0, -1).forEach((n) => {
      n.remove();
      removed += 1;
    });
  };
  for (const { kind, key } of META_KEYS) {
    drop(Array.from(doc.head.querySelectorAll(selector(kind, key))));
  }
  drop(Array.from(doc.head.querySelectorAll('link[rel="canonical"]')));
  drop(Array.from(doc.head.querySelectorAll("title")));
  return removed;
}

function setMeta(doc: Doc, kind: "name" | "property", key: string, value: string) {
  const all = doc.head.querySelectorAll<HTMLMetaElement>(selector(kind, key));
  let el = all.length ? all[all.length - 1]! : null;
  if (!el) {
    el = doc.createElement("meta");
    el.setAttribute(kind, key);
    doc.head.appendChild(el);
  }
  if (el.getAttribute("content") !== value) el.setAttribute("content", value);
}

function clean(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  return s.length ? s : null;
}

/**
 * Applies an admin override to the live document. Blank fields are left alone
 * so the page's own metadata keeps working. Always dedupes, even when there is
 * no override for the path.
 */
export function applySeoOverrideToDocument(
  doc: Doc,
  override: PageSeoOverride | null | undefined,
): void {
  dedupeHeadTags(doc);
  if (!override) return;

  const title = clean(override.title);
  const description = clean(override.description);
  const ogTitle = clean(override.og_title) ?? title;
  const ogDescription = clean(override.og_description) ?? description;
  const ogImage = clean(override.og_image);
  const canonical = clean(override.canonical);

  if (title && doc.title !== title) doc.title = title;
  if (description) setMeta(doc, "name", "description", description);
  if (ogTitle) {
    setMeta(doc, "property", "og:title", ogTitle);
    setMeta(doc, "name", "twitter:title", ogTitle);
  }
  if (ogDescription) {
    setMeta(doc, "property", "og:description", ogDescription);
    setMeta(doc, "name", "twitter:description", ogDescription);
  }
  if (ogImage) {
    setMeta(doc, "property", "og:image", ogImage);
    setMeta(doc, "name", "twitter:image", ogImage);
  }
  if (canonical) {
    const links = doc.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]');
    let link = links.length ? links[links.length - 1]! : null;
    if (!link) {
      link = doc.createElement("link");
      link.setAttribute("rel", "canonical");
      doc.head.appendChild(link);
    }
    if (link.getAttribute("href") !== canonical) link.setAttribute("href", canonical);
    setMeta(doc, "property", "og:url", canonical);
  }
  if (override.noindex) setMeta(doc, "name", "robots", "noindex,nofollow");
}
